import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { Experience, Education } from '../../types';

const ResumeSection: React.FC = () => {
  const { themeConfig } = useTheme();

  const experienceData: Experience[] = [
    {
      id: 'charyx-labs',
      company: 'Charyx Labs (charyxlabs.tech) — Remote',
      position: 'Founder & Proprietor',
      startDate: 'Nov 2024',
      endDate: 'Present',
      description: [
        'Sole founder and proprietor of an AI product studio: automation, AI systems and full product engineering.',
        'Supported 13+ businesses across 4 countries (US, Japan, Australia, India) at a 4.8★ average rating.',
        'Typical first automation goes live in about a week — CRM, lead capture, booking and voice AI receptionists.',
        'Shipped Life Search AI (on-device OCR + Claude) and DocuMind (local-first, on-device RAG).',
        'Productized an Operations Portal for sales teams (15 screens, Rep / Team / Admin), first built for PowerSmith Solar.',
      ],
      technologies: ['n8n', 'GoHighLevel', 'Claude', 'FastAPI', 'React', 'Voice AI'],
    },
    {
      id: 'powersmith',
      company: 'PowerSmith Energy (San Diego) — Remote',
      position: 'AI Full-Stack Developer (contract)',
      startDate: 'Jul 2026',
      endDate: 'Present',
      description: [
        'Built the React + Vite marketing site and a FastAPI + MongoDB operations portal with Rep / Team / Admin access.',
        'Referral and affiliate program mirrored into GoHighLevel, plus a commission engine with server-generated PDF statements.',
        'Org chart, training, SOPs, and digital business cards with QR codes for every rep.',
        'AI content tooling on the Anthropic SDK with structured outputs; media on Cloudflare R2.',
      ],
      technologies: ['React', 'FastAPI', 'MongoDB', 'Claude', 'GoHighLevel', 'Railway'],
    },
    {
      id: 'onoffdeals',
      company: 'OnOffDeals (onoffdeals.ai) — Remote',
      position: 'Sole Developer — AI Real-Estate SaaS',
      startDate: '2025',
      endDate: 'Present',
      description: [
        'Built and operate the whole product: React 18 SPA plus an async FastAPI + MongoDB backend.',
        '16 API routers, 20 domain services and 311 automated tests guarding a from-scratch valuation engine.',
        'Live Stripe billing — subscriptions, trials, coupons, webhook-driven state and per-plan usage metering.',
        'Integrated Anthropic Claude as an in-app deal coach with prompt guardrails and output validation.',
      ],
      technologies: ['FastAPI', 'MongoDB', 'Redis', 'Stripe', 'Claude', 'React'],
    },
    {
      id: 'axiom-realty',
      company: 'Axiom Realty Solutions LLC (Chicago) — Remote',
      position: 'Python IT & Automation Engineer',
      startDate: 'Aug 2025',
      endDate: 'Present',
      description: [
        'Automated large-scale property-data acquisition with Selenium, the Zillow API and Apify.',
        'Built ETL pipelines across CRM dashboards and Google Sheets, cutting manual workload ~70%.',
        'Administer company IT: GoHighLevel workflows, VPS, Google Workspace, domains and cloud APIs.',
      ],
      technologies: ['Python', 'Selenium', 'Apify', 'ETL', 'GoHighLevel'],
    },
    {
      id: 'podiam-ai',
      company: 'Podiam AI — Remote',
      position: 'Software Engineer',
      startDate: 'Dec 2025',
      endDate: 'Feb 2026',
      description: [
        'Architected a serverless LaTeX PDF pipeline with multi-API fallback.',
        'Fixed data-integrity bugs where raw LangChain metadata leaked into generated documents.',
        'Ran a full Django / PostgreSQL / Redis / Celery stack and built TypeScript API integration layers.',
      ],
      technologies: ['Django', 'PostgreSQL', 'Celery', 'LangChain', 'TypeScript'],
    },
    {
      id: 'trinzz-intern',
      company: 'Trinzz — Remote',
      position: 'Software Development Intern',
      startDate: 'Nov 2024',
      endDate: 'Dec 2025',
      description: [
        'Built real-time facial-recognition systems (DeepFace, OpenCV) at 95%+ accuracy on image and video.',
        'Built AI-based DICOM processing for biomedical imaging workflows.',
        'Maintained and optimized the company’s React website, reducing load times ~15%.',
      ],
      technologies: ['AI', 'React', 'DeepFace', 'OpenCV', 'DICOM'],
    },
    {
      id: 'infosys-intern',
      company: 'Infosys Springboard — Remote',
      position: 'Artificial Intelligence Intern',
      startDate: 'Oct 2024',
      endDate: 'Dec 2024',
      description: [
        'Built an AI-powered Personal Stylist using a hybrid DNN (content-based + collaborative filtering).',
        'Reached 87% recommendation accuracy with TensorFlow/Keras.',
      ],
      technologies: ['AI', 'TensorFlow', 'Recommender Systems', 'Agile'],
    },
  ];

  const freelanceData: Experience[] = [
    {
      id: 'freelance-upwork',
      company: 'Freelancer (Upwork & direct clients)',
      position: 'Automation & Python Freelancer',
      startDate: 'Jan 2024',
      endDate: 'Present',
      description: [
        '100% Job Success across 11 jobs and 80+ hours of automation, AI and Python work.',
        'Delivered automation tools for data extraction, preprocessing and web scraping.',
        'Built a ChatGPT-based Excel Prompt-Maker app so non-technical users could automate spreadsheets.',
        'See my Upwork profile: https://www.upwork.com/freelancers/~01f16adc9d2f50d399',
      ],
      technologies: ['n8n', 'Python', 'GPT API', 'Automation', 'Web Scraping'],
    },
    {
      id: 'multi-brand-marketing',
      company: 'BrightLane Property · SharpEdge Property · USA Roofing Supply',
      position: 'Paid Media, Social & CRM Manager',
      startDate: '2024',
      endDate: 'Present',
      description: [
        'Run Meta and Google Ads for real-estate lead generation across three brands.',
        'Connect ad traffic to GoHighLevel (webhooks, A2P 10DLC, chat widget) for click-to-conversation tracking.',
        'Own content strategy, publishing and community for each brand’s distinct identity.',
      ],
      technologies: ['Meta Ads', 'Google Ads', 'GoHighLevel', 'GA4', 'A2P 10DLC'],
    },
  ];

  const educationData: Education[] = [
    {
      id: 'btech-ai-ml',
      institution: 'Guru Gobind Singh Indraprastha University',
      degree: 'B.Tech',
      field: 'Artificial Intelligence & Machine Learning',
      startDate: '2023',
      endDate: '2027',
      location: 'New Delhi, India',
      achievements: ['CGPA: 9.422 (current)'],
    },
  ];

  const claudeCredential = {
    title: 'Claude Certified Developer – Foundations',
    date: 'Sep 2026',
    detail:
      'Proctored exam on building and shipping production apps and agents on Claude: the Claude API, Claude Code, custom tools and MCP servers. Passed with 896 / 1000 (pass mark 720), valid through Sep 2027. I then turned my study plan into a free prep course on Gurukin.',
    links: [
      { href: 'https://www.credly.com/badges/1470a632-1d6f-4040-999f-f3962817b0d6', label: 'Verify on Credly' },
      { href: 'https://gurukin.app/ccdv-f/', label: 'See the prep course' },
    ],
  };

  const certifications = [
    { title: 'Machine Learning Specialization', issuer: 'Stanford University', date: 'Jul 2024' },
    { title: 'Generative AI & LLMs', issuer: 'Google', date: 'May 2024' },
    { title: 'AI Fundamentals', issuer: 'Credential ID: AIF0026070H14534', date: 'Feb 2024' },
    { title: 'Supervised ML: Regression/Classification', issuer: 'Coursera', date: 'Jan 2024' },
    { title: 'Responsive Web Design', issuer: 'freeCodeCamp', date: 'Jul 2023' },
  ];

  const achievements = [
    {
      title: 'Winner (1st) — ISRO Bhartiya Antariksh Hackathon 2025',
      description:
        'Among 8,744 teams; awarded at Bharat Mandapam, New Delhi by the ISRO Chairman & Dr. Jitendra Singh.',
      date: '2025',
    },
    {
      title: 'Hackathon Mentor',
      description: 'Invited to mentor participants in AI/ML development and problem-solving.',
      date: '2024–2025',
    },
    {
      title: 'EF SET English — C2 Proficient',
      description: '73/100, demonstrating advanced English proficiency.',
      date: 'Jun 2025',
    },
  ];

  const container = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.15 } },
  };
  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const renderTimeline = (data: Experience[], dotColor: string) => (
    <motion.div
      className="space-y-6"
      variants={container}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: '-80px' }}
    >
      {data.map((exp) => (
        <motion.div key={exp.id} className="relative pl-8" variants={item}>
          <span
            className="absolute left-0 top-6 h-3.5 w-3.5 rounded-full border-2 border-canvas-base"
            style={{ background: dotColor, boxShadow: `0 0 12px ${dotColor}` }}
          />
          <span className="absolute left-[6px] top-9 h-[calc(100%-1rem)] w-px bg-canvas-border/60" />
          <div className="n8n-node p-5">
            <div className="mb-2 flex flex-col md:flex-row md:items-start md:justify-between">
              <h4 className="font-display text-lg font-semibold text-ink-main">
                {exp.position}
              </h4>
              <span className="font-mono text-xs text-neon-cyan">
                {exp.startDate} — {exp.endDate}
              </span>
            </div>
            <p className="mb-3 text-sm text-ink-soft">{exp.company}</p>
            <ul className="mb-4 space-y-1.5">
              {exp.description.map((desc, idx) =>
                desc.startsWith('See my Upwork profile:') ? (
                  <li key={idx} className="flex gap-2 text-sm text-ink-soft">
                    <span className="text-neon-green">▹</span>
                    <a
                      href="https://www.upwork.com/freelancers/~01f16adc9d2f50d399"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-neon-cyan hover:underline"
                    >
                      See my Upwork profile
                    </a>
                  </li>
                ) : (
                  <li key={idx} className="flex gap-2 text-sm text-ink-soft">
                    <span className="text-neon-green">▹</span>
                    {desc}
                  </li>
                )
              )}
            </ul>
            {exp.technologies && (
              <div className="flex flex-wrap gap-1.5">
                {exp.technologies.map((tech, idx) => (
                  <span key={idx} className="tech-chip !py-1 !text-[11px]">
                    {tech}
                  </span>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      ))}
    </motion.div>
  );

  return (
    <section id="resume" className={themeConfig.layout?.sectionStyle}>
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.6 }}
        className="mb-12 flex flex-col gap-6 md:flex-row md:items-end md:justify-between"
      >
        <div className="max-w-2xl">
          <div className="mb-3 flex items-center gap-2">
            <span className="h-px w-6 bg-neon-cyan/60" />
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-neon-cyan">
              {'// track record'}
            </span>
          </div>
          <h2 className="font-display text-3xl font-bold leading-tight text-ink-main sm:text-4xl">
            Experience &amp; <span className="gradient-text">credibility</span>
          </h2>
          <p className="mt-3 text-lg text-ink-soft">
            The background behind the builds: roles, education, credentials and wins.
          </p>
        </div>
        <a href="/resume.pdf" download className="neon-btn-ghost shrink-0">
          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
          Download resume
        </a>
      </motion.div>

      <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
        <div>
          <h3 className="mb-6 font-mono text-sm uppercase tracking-widest text-neon-magenta">
            work experience
          </h3>
          {renderTimeline(experienceData, '#22e6ff')}
        </div>
        <div>
          <h3 className="mb-6 font-mono text-sm uppercase tracking-widest text-neon-green">
            freelance
          </h3>
          {renderTimeline(freelanceData, '#2bffb0')}

          <h3 className="mb-6 mt-10 font-mono text-sm uppercase tracking-widest text-neon-purple">
            education
          </h3>
          <motion.div
            className="space-y-6"
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
          >
            {educationData.map((edu) => (
              <motion.div key={edu.id} className="n8n-node p-5" variants={item}>
                <div className="mb-1 flex flex-col md:flex-row md:items-start md:justify-between">
                  <h4 className="font-display text-lg font-semibold text-ink-main">
                    {edu.degree} · {edu.field}
                  </h4>
                  <span className="font-mono text-xs text-neon-cyan">
                    {edu.startDate} — {edu.endDate}
                  </span>
                </div>
                <p className="text-sm text-ink-soft">{edu.institution}</p>
                {edu.location && (
                  <p className="text-xs text-ink-muted">{edu.location}</p>
                )}
                {edu.achievements?.map((a, i) => (
                  <p key={i} className="mt-2 font-mono text-xs text-neon-green">
                    {a}
                  </p>
                ))}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Certifications */}
      <div className="mt-14">
        <h3 className="mb-6 font-mono text-sm uppercase tracking-widest text-neon-cyan">
          certifications
        </h3>

        {/* Spotlight: the Anthropic credential */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
          className="n8n-node mb-4 flex flex-col gap-5 p-6 md:flex-row md:items-center md:justify-between"
          style={{
            borderColor: '#d9642c66',
            background: 'linear-gradient(120deg, rgba(217, 100, 44, 0.12), transparent 60%)',
          }}
        >
          <div className="flex items-start gap-4">
            <img
              src="/assets/logos/anthropic.svg"
              alt="Anthropic"
              width={44}
              height={44}
              className="mt-1 shrink-0 rounded-lg border border-canvas-border bg-canvas-deep p-2"
              style={{ width: 44, height: 44 }}
            />
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest" style={{ color: '#d9642c' }}>
                featured credential · anthropic · {claudeCredential.date}
              </p>
              <p className="mt-1 font-display text-xl font-bold text-ink-main">
                {claudeCredential.title}
              </p>
              <p className="mt-1.5 max-w-2xl text-sm text-ink-soft">{claudeCredential.detail}</p>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap gap-3 md:flex-col">
            {claudeCredential.links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                className="neon-btn-ghost justify-center"
              >
                {l.label}
              </a>
            ))}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {certifications.map((cert, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.05 }}
              className="n8n-node p-4"
            >
              <p className="font-display text-sm font-semibold text-ink-main">
                {cert.title}
              </p>
              <p className="mt-1 text-xs text-ink-soft">{cert.issuer}</p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-wide text-ink-muted">
                {cert.date}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Achievements */}
      <div className="mt-14">
        <h3 className="mb-6 font-mono text-sm uppercase tracking-widest text-neon-amber">
          achievements
        </h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {achievements.map((a, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.06 }}
              className={`sticky-note ${['amber', 'cyan', 'magenta'][idx % 3]}`}
            >
              <div className="mb-1 flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-widest opacity-70">
                  win
                </span>
                <span className="font-mono text-[10px] text-ink-muted">{a.date}</span>
              </div>
              <h4 className="font-display text-sm font-semibold text-ink-main">
                {a.title}
              </h4>
              <p className="mt-1.5 text-xs text-ink-soft">{a.description}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* YouTube */}
      <div className="mt-14">
        <h3 className="mb-6 font-mono text-sm uppercase tracking-widest text-neon-magenta">
          on youtube
        </h3>
        <div className="n8n-node flex flex-col items-center gap-6 p-6 md:flex-row">
          <iframe
            width="400"
            height="225"
            src="https://www.youtube.com/embed/S4ZPeb96LGw"
            title="Featured YouTube Video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full max-w-md rounded-xl border border-canvas-border"
            style={{ background: '#000' }}
          ></iframe>
          <div>
            <p className="mb-3 text-ink-soft">
              I share automation builds, AI experiments and tutorials on my
              channel.
            </p>
            <a
              href="https://www.youtube.com/@sharmachaitanya945"
              target="_blank"
              rel="noopener noreferrer"
              className="neon-btn"
            >
              Visit my channel
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResumeSection;
